import React, { useState, useEffect } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, IconButton, Typography, Box, TextField, FormControl, MenuItem, OutlinedInput, Select } from '@mui/material';
import { ModalClose } from '../assets/icons';
import TickIcon from '../assets/icons/mdiTickIcon';
import { toast } from 'react-toastify';
import AuthService from '../services/auth.service';
import { decodeMetadataToken } from '../utils/token.utils';

interface UpdateDomainModalProps {
    open: boolean;
    onClose: () => void;
    domain: string;
    onConfirm: (newDomain: string) => void;
    initialValue: string;
    application: string;
}

const UpdateDomainModal: React.FC<UpdateDomainModalProps> = ({ open, onClose, onConfirm, domain, initialValue, application }) => {
    const [domainValue, setDomainValue] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [status, setStatus] = useState("active")

    useEffect(() => {
        if (open) {
            setDomainValue(initialValue || '');
            setError('');
        }
    }, [open, initialValue]);

    const handleDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setDomainValue(value);
        
        // Validation
        if (!value) {
            setError('Domain name is required');
        } else if (!/^[a-zA-Z0-9-]+$/.test(value)) {
            setError('Only letters, numbers, and hyphens are allowed');
        } else {
            setError('');
        }
    };

    const handleConfirm = async () => {
        if (!error && domainValue) {
            setIsLoading(true);
            const metadata = decodeMetadataToken()
            const companyId = metadata?.companies?.[0]?.id
            try {
                const response = await AuthService.updateCompanyDomain(
                    companyId,
                    domainValue,
                    application
                );

                console.log(response)
                
                if (response.success) {
                    toast.success(response.message);
                    onConfirm(response.domain);
                    onClose();
                } else {
                    toast.error(response.message);
                }
            } catch (error: any) {
                console.log(error)
                toast.error(error.message);
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            className='card-details-modal' 
            sx={{ '& .MuiDialog-paper': { width: '661px', maxWidth: '661PX', borderRadius: '24px', border: '1px solid #E8E8EA' } }}
        >
            <DialogTitle sx={{ m: 0, }} p={{ xs: "0 16px 16px", sm: "0 32px 32px" }}>
                <Typography color='#333333' fontSize={{ xs: "22px", sm: "28px" }} fontWeight='500'>Edit Domain</Typography>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={() => ({
                        position: 'absolute',
                        right: { xs: "17px" },
                        top: { xs: "15px" },
                    })}
                >
                    <ModalClose />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ borderTop: 'none', borderBottom: 'none', p: { xs: "0 16px 16px", sm: "0 32px 32px" } }}>
                <Box bgcolor="rgba(97, 78, 250, 0.08);" borderRadius="8px" padding="12px 10px" mb="40px">
                    <Typography component="span" fontWeight="600" color="#2B2B30" fontSize="16px">
                        Note:<Typography component="span" ml="4px">Special character's are not allowed in domain name. Please use numbers, letters or hyphen. Do not add www or extension like .com, .net, etc.</Typography>
                    </Typography>
                </Box>
                <Typography variant="body1" color="#333333" mb="10px" sx={{ opacity: '0.6' }}>Sub Domain</Typography>
                <Box display="flex" alignItems="center">
                    <Typography color="#333" fontSize="16px" fontWeight="500" mr="12px">https://</Typography>
                    <TextField
                        variant="outlined"
                        placeholder='your-domain'
                        value={domainValue}
                        onChange={handleDomainChange}
                        error={!!error}
                        helperText={error}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: '8px',
                                borderColor: '#C7C7C7',
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: '#333333',
                                border: '1px solid'
                            },
                            "& .MuiOutlinedInput-input": {
                                padding: '14px 16px',
                            },
                            "&.MuiInputBase-input::placeholder": {
                                color: '#333333',
                                fontSize: '16px',
                                opacity: '0.8'
                            },
                            "& .MuiFormHelperText-root": {
                                color: '#E44258',
                                marginLeft: 0,
                                marginTop: '8px'
                            }
                        }}
                        fullWidth
                    />
                    <Box display="flex">
                        <Typography color="#333" fontSize="16px" fontWeight="500" margin="0 12px">{domain}</Typography>
                        <TickIcon />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ mb: { xs: "16px", sm: "32px" }, mr: { xs: "11px", sm: "32px" } }}>
                <Button
                    onClick={onClose}
                    color="primary"
                    disabled={isLoading}
                    sx={{
                        border: '1px solid #E8EAF0',
                        px: '22px',
                        color: '#2B2B30 !important',
                        textTransform: 'capitalize',
                        borderRadius: '8px',
                        mr: '16px',
                        '&:hover': {
                            border: '1px solid #E8EAF0 !important',
                            boxShadow: 'unset !important'
                        },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    disabled={!!error || !domainValue || isLoading}
                    variant="contained"
                    size='large'
                    sx={{
                        borderRadius: '8px',
                        '&.Mui-disabled': {
                            bgcolor: '#E8EAF0',
                            color: '#8F8F8F !important'
                        }
                    }}
                >
                    {isLoading ? 'Updating...' : 'Update'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UpdateDomainModal;
